import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getCategories } from '../../api/client';

// Path A vs Path B cost analysis — client-only, localStorage. Answers two questions:
// (1) which path costs less over N months, (2) how much cash each path needs up front.

const STORAGE_KEY = 'ledger:cost-compare';
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rid = () => Math.random().toString(36).slice(2, 10);
const blankRow = () => ({ id: rid(), name: '', amount: '', categoryId: null });
const sumRows = (rows) => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

const blankPath = (name) => ({
  name,
  oneTime: [blankRow()],
  monthly: [blankRow()],
});

const blankSheet = () => ({
  horizon: 4,
  pathA: blankPath('Path A'),
  pathB: blankPath('Path B'),
});

const housingSheet = () => ({
  horizon: 4,
  pathA: {
    name: 'Stay',
    oneTime: [blankRow()],
    monthly: [
      { id: rid(), name: 'Rent', amount: '', categoryId: null },
      { id: rid(), name: 'Utilities', amount: '', categoryId: null },
    ],
  },
  pathB: {
    name: 'Leave',
    oneTime: [
      { id: rid(), name: 'Break fee', amount: '', categoryId: null },
      { id: rid(), name: 'Moving', amount: '', categoryId: null },
    ],
    monthly: [blankRow()],
  },
});

const loadSaved = () => {
  try {
    const d = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (d?.pathA && d?.pathB && Number(d.horizon) > 0) return d;
  } catch (e) { /* corrupt */ }
  return null;
};

export const horizonTotal = (path, horizon) =>
  sumRows(path.oneTime || []) + sumRows(path.monthly || []) * Math.max(0, Number(horizon) || 0);

export const upfrontCash = (path) => sumRows(path.oneTime || []);

// Smallest whole months where the path with lower monthly wins despite higher one-time.
// Returns null when not applicable (same monthly, or already cheaper at month 0, etc.).
export const breakevenMonths = (pathA, pathB) => {
  const a0 = sumRows(pathA.oneTime || []);
  const b0 = sumRows(pathB.oneTime || []);
  const am = sumRows(pathA.monthly || []);
  const bm = sumRows(pathB.monthly || []);
  if (am === bm) return null;
  // Solve a0 + am*m = b0 + bm*m  →  m = (b0 - a0) / (am - bm)
  const m = (b0 - a0) / (am - bm);
  if (!Number.isFinite(m) || m < 0) return null;
  const ceil = Math.ceil(m - 1e-9);
  if (ceil < 1) return null;
  return ceil;
};

const CostCompare = () => {
  const { COLORS } = useTheme();
  const [sheet, setSheet] = useState(loadSaved);
  const [categories, setCategories] = useState([]);
  const [pullFor, setPullFor] = useState(null); // { pathKey, side, rowId } | null

  useEffect(() => {
    if (!sheet) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sheet));
  }, [sheet]);

  useEffect(() => {
    getCategories()
      .then((r) => {
        const d = r.data;
        const list = Array.isArray(d) ? d : d?.data || [];
        setCategories(list.filter((c) => c.is_active !== false && c.type !== 'cc_payment' && !c.exclude_from_budget));
      })
      .catch(() => {});
  }, []);

  const btn = (primary) => ({
    background: primary ? COLORS.accent : 'transparent',
    color: primary ? '#fff' : COLORS.textMuted,
    border: primary ? 'none' : `1px solid ${COLORS.border}`,
    borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
  });

  const startBlank = () => setSheet(blankSheet());
  const startHousing = () => setSheet(housingSheet());
  const startOver = () => {
    if (!window.confirm('Wipe this compare sheet?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setSheet(null);
    setPullFor(null);
  };

  const setHorizon = (v) => {
    const n = Math.max(1, Math.min(120, parseInt(v, 10) || 1));
    setSheet((s) => ({ ...s, horizon: n }));
  };

  const setPathName = (pathKey, name) =>
    setSheet((s) => ({ ...s, [pathKey]: { ...s[pathKey], name } }));

  const setCell = (pathKey, side, rowId, field, value) =>
    setSheet((s) => ({
      ...s,
      [pathKey]: {
        ...s[pathKey],
        [side]: s[pathKey][side].map((r) => (r.id === rowId ? { ...r, [field]: value } : r)),
      },
    }));

  const addRow = (pathKey, side) =>
    setSheet((s) => ({
      ...s,
      [pathKey]: { ...s[pathKey], [side]: [...s[pathKey][side], blankRow()] },
    }));

  const delRow = (pathKey, side, rowId) =>
    setSheet((s) => ({
      ...s,
      [pathKey]: {
        ...s[pathKey],
        [side]: s[pathKey][side].length <= 1
          ? [blankRow()]
          : s[pathKey][side].filter((r) => r.id !== rowId),
      },
    }));

  const applyCategory = (cat) => {
    if (!pullFor) return;
    const { pathKey, side, rowId } = pullFor;
    setSheet((s) => ({
      ...s,
      [pathKey]: {
        ...s[pathKey],
        [side]: s[pathKey][side].map((r) =>
          r.id === rowId
            ? { ...r, name: r.name.trim() || cat.name, amount: String(parseFloat(cat.amount || 0)), categoryId: cat.id }
            : r
        ),
      },
    }));
    setPullFor(null);
  };

  if (!sheet) {
    return (
      <div style={{ textAlign: 'center', padding: '44px 16px', fontFamily: "'DM Sans', sans-serif" }}>
        <h3 style={{ color: COLORS.text, fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>Compare two paths</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 13, maxWidth: 440, margin: '0 auto 20px', lineHeight: 1.6 }}>
          Weigh one-time costs and monthly costs over a time horizon — stay vs leave,
          keep vs replace, buy vs rent. See which costs less overall and how much cash
          each path needs up front. Saved on this device only.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={startBlank} style={btn(true)}>Start blank</button>
          <button onClick={startHousing} style={btn(false)}>Housing / leave template</button>
        </div>
      </div>
    );
  }

  const { horizon, pathA, pathB } = sheet;
  const totalA = horizonTotal(pathA, horizon);
  const totalB = horizonTotal(pathB, horizon);
  const upfrontA = upfrontCash(pathA);
  const upfrontB = upfrontCash(pathB);
  const net = totalB - totalA; // negative ⇒ B cheaper
  const cheaperKey = net < 0 ? 'B' : net > 0 ? 'A' : null;
  const cheaperName = cheaperKey === 'A' ? pathA.name : cheaperKey === 'B' ? pathB.name : null;
  const savings = Math.abs(net);
  const be = breakevenMonths(pathA, pathB);

  const cellInput = {
    background: 'transparent', border: '1px solid transparent', borderRadius: 6,
    color: COLORS.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', padding: '7px 8px',
  };

  const rowEditor = (pathKey, side, rows) => (
    <>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: `1px solid ${COLORS.border}`, padding: '1px 8px' }}>
          <input
            value={r.name}
            placeholder="Label"
            onChange={(e) => setCell(pathKey, side, r.id, 'name', e.target.value)}
            style={{ ...cellInput, flex: 1, minWidth: 0, fontWeight: 500 }}
          />
          {side === 'monthly' && (
            <button
              type="button"
              onClick={() => setPullFor({ pathKey, side, rowId: r.id })}
              title="Pull amount from a budget category"
              style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.accent, cursor: 'pointer', fontSize: 10, fontWeight: 700, padding: '4px 6px', fontFamily: 'inherit', flexShrink: 0 }}
            >Budget</button>
          )}
          <input
            value={r.amount}
            placeholder="0"
            inputMode="decimal"
            onChange={(e) => setCell(pathKey, side, r.id, 'amount', e.target.value.replace(/[^0-9.-]/g, ''))}
            onFocus={(e) => e.target.select()}
            style={{ ...cellInput, width: 96, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}
          />
          <button onClick={() => delRow(pathKey, side, r.id)} aria-label="Delete row" style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
      ))}
      <button onClick={() => addRow(pathKey, side)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: COLORS.accent, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '9px 14px' }}>
        + Add row
      </button>
    </>
  );

  const pathCard = (pathKey, path, total, upfront) => (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ padding: '10px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
        <input
          value={path.name}
          onChange={(e) => setPathName(pathKey, e.target.value)}
          style={{ width: '100%', background: 'transparent', border: 'none', color: COLORS.text, fontSize: 15, fontWeight: 800, fontFamily: 'inherit', outline: 'none', padding: 0 }}
        />
      </div>

      <div style={{ padding: '8px 14px 4px' }}>
        <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>One-time (up front)</div>
      </div>
      {rowEditor(pathKey, 'oneTime', path.oneTime)}

      <div style={{ padding: '8px 14px 4px', borderTop: `1px solid ${COLORS.border}` }}>
        <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Monthly × {horizon}</div>
      </div>
      {rowEditor(pathKey, 'monthly', path.monthly)}

      <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>Cash up front</span>
          <span style={{ color: COLORS.amber, fontFamily: 'monospace', fontSize: 14, fontWeight: 800 }}>{fmt(upfront)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>Total over {horizon} mo</span>
          <span style={{ color: COLORS.text, fontFamily: 'monospace', fontSize: 14, fontWeight: 800 }}>{fmt(total)}</span>
        </div>
      </div>
    </div>
  );

  const sortedCats = [...categories].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 700 }}>Horizon</label>
          <input
            type="number"
            min={1}
            max={120}
            value={horizon}
            onChange={(e) => setHorizon(e.target.value)}
            style={{ width: 64, padding: '7px 10px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 14, fontWeight: 700, fontFamily: 'monospace', outline: 'none' }}
          />
          <span style={{ color: COLORS.textMuted, fontSize: 12 }}>months</span>
        </div>
        <button onClick={startOver} style={{ ...btn(false), color: COLORS.red }}>Start over</button>
      </div>
      <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '0 0 14px', lineHeight: 1.45 }}>
        One-time costs = cash needed up front. Monthly costs run for the whole horizon.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 14 }}>
        {pathCard('pathA', pathA, totalA, upfrontA)}
        {pathCard('pathB', pathB, totalB, upfrontB)}
      </div>

      <div style={{
        background: cheaperKey ? (COLORS.greenGlow || 'rgba(34,197,94,0.08)') : COLORS.surface,
        border: `1px solid ${cheaperKey ? (COLORS.green + '44') : COLORS.border}`,
        borderRadius: 12, padding: '14px 16px',
      }}>
        {cheaperKey ? (
          <>
            <div style={{ color: COLORS.green, fontSize: 14, fontWeight: 800, marginBottom: 6 }}>
              {cheaperName || `Path ${cheaperKey}`} costs {fmt(savings)} less over {horizon} months
            </div>
            <div style={{ color: COLORS.text, fontSize: 13, lineHeight: 1.5 }}>
              Choosing <strong>{pathA.name || 'Path A'}</strong> needs {fmt(upfrontA)} up front.
              Choosing <strong>{pathB.name || 'Path B'}</strong> needs {fmt(upfrontB)} up front.
            </div>
          </>
        ) : (
          <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}>
            Both paths cost the same over {horizon} months ({fmt(totalA)})
          </div>
        )}
        {be != null && (
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 8, lineHeight: 1.45 }}>
            Break-even around month {be}: after that, the path with lower monthly costs pulls ahead.
          </div>
        )}
        {cheaperKey && ((cheaperKey === 'A' && upfrontA > upfrontB) || (cheaperKey === 'B' && upfrontB > upfrontA)) && (
          <div style={{ color: COLORS.amber, fontSize: 12, marginTop: 8, lineHeight: 1.45, fontWeight: 600 }}>
            Cheaper over the horizon, but needs more cash up front — make sure you can float it.
          </div>
        )}
      </div>

      {pullFor && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}
          onClick={(e) => { if (e.target === e.currentTarget) setPullFor(null); }}
        >
          <div style={{ width: '100%', maxHeight: '70vh', overflowY: 'auto', background: COLORS.surface, borderRadius: '16px 16px 0 0', padding: '20px 16px 32px', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ width: 36, height: 4, background: COLORS.border, borderRadius: 99, margin: '0 auto 16px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 700, margin: 0 }}>Pull from budget</h3>
              <button type="button" onClick={() => setPullFor(null)} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Close</button>
            </div>
            {sortedCats.length === 0 ? (
              <p style={{ color: COLORS.textMuted, fontSize: 13 }}>No categories found.</p>
            ) : sortedCats.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => applyCategory(c)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 8, padding: '12px 4px', border: 'none', borderBottom: `1px solid ${COLORS.border}`, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
              >
                <span style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                <span style={{ color: COLORS.textMuted, fontFamily: 'monospace', fontSize: 13 }}>{fmt(c.amount)}/mo · {c.type}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CostCompare;
